import lozad from 'lozad';

import ElementController from '../base-controllers/ElementController';
import { getData, isElement } from '../utils/dom';

const LAZY_IMG_SELECTOR = 'img[loading="lazy"]';
const LAZY_BACKGROUND_SELECTOR = 'div[loading="lazy"]';

/**
 * Lazily load stuff natively or with 'lozad'.
 *
 * Only used for images right now.
 *
 * Set `data-src` and `loading=lazy` attributes to enable lazy loading.
 *
 * Lazy load an image:
 *
 *     <img data-src="..." loading="lazy" data-controller="LazyLoad">
 *
 * Lazy load a background image:
 *
 *     <div
 *        data-background-image="..."
 *        role="img"
 *        aria-label="..."
 *        loading="lazy"
 *        data-controller="LazyLoad">...</div>
 *
 * Use the 'img' template tag to render this HTML.
 */
export default class LazyLoad extends ElementController {
  static id = 'LazyLoad';

  init() {
    this.initLazyLoad();
  }

  initLazyLoad() {
    let node;
    if (isElement(this.getRoot(), null, { loading: 'lazy' })) {
      node = this.getRoot();
    } else {
      node = this.getSingleNode(
        `${LAZY_IMG_SELECTOR},${LAZY_BACKGROUND_SELECTOR}`,
      );
    }

    // Don't try loading a missing file
    if (getData(node, 'missing', Boolean)) {
      return;
    }

    // Use native lazy loading for <img> when there is support for it
    if (isElement(node, 'img') && 'loading' in HTMLImageElement.prototype) {
      node.src = node.dataset.src;
    } else {
      // Initialize lozad for everything else
      const observer = lozad(node);
      observer.observe();
    }
  }
}
